import { useState, useEffect } from 'react';
import { Printer, FileText, StickyNote } from 'lucide-react';
import { docExtras } from '../../lib/docExtras';

/**
 * The terminal's "this receipt has extra text — print it?" sheet.
 *
 * The desk version is a dialog; this one is a full-height overlay with tap-sized
 * rows, because the terminal is a handheld used one-thumbed at the counter.
 * Same rules underneath (lib/docExtras), same ruling: NO "remember this choice"
 * — the answer is per print, not a setting.
 *
 * A document with nothing extra never opens this; callers check `hasExtras`
 * first and print straight through.
 *
 * Props: open, doc, onCancel, onConfirm(extras)
 */
export function hasExtras(doc) {
  return docExtras(doc).hasAny;
}

export default function TerminalPrintExtrasSheet({ open, doc, onCancel, onConfirm }) {
  const extras = docExtras(doc);
  const [wantDescriptions, setWantDescriptions] = useState(true);
  const [wantNote, setWantNote] = useState(true);

  useEffect(() => {
    if (open) { setWantDescriptions(true); setWantNote(true); }
  }, [open]);

  if (!open || !extras.hasAny) return null;

  const Row = ({ testId, icon, title, detail, checked, onToggle }) => (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testId}
      className={`w-full text-left px-4 py-3.5 rounded-xl border-2 flex items-start gap-3 active:scale-[0.99] transition-all ${
        checked ? 'border-[#1A4D2E] bg-emerald-50' : 'border-slate-200 bg-white'
      }`}
    >
      <span
        className={`mt-0.5 w-6 h-6 rounded-md border-2 shrink-0 flex items-center justify-center text-white text-sm font-bold ${
          checked ? 'border-[#1A4D2E] bg-[#1A4D2E]' : 'border-slate-300 bg-white'
        }`}
      >
        {checked ? '✓' : ''}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-[15px] font-semibold text-slate-800">
          {icon}
          {title}
        </span>
        <span className="block text-xs text-slate-500 mt-0.5 break-words">{detail}</span>
      </span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end" data-testid="terminal-print-extras">
      <div className="w-full bg-white rounded-t-2xl p-4 space-y-3 max-h-[85vh] overflow-y-auto">
        <div>
          <p className="text-base font-bold text-slate-800">This receipt has extra text</p>
          <p className="text-xs text-slate-500 mt-0.5">Choose what to print.</p>
        </div>

        {extras.hasDescriptions && (
          <Row
            testId="terminal-extras-descriptions"
            icon={<FileText size={14} className="text-slate-400" />}
            title="Item descriptions"
            detail={`${extras.descriptions.length} item${extras.descriptions.length > 1 ? 's' : ''} · ${extras.descriptions
              .map((d) => `"${d.text}"`)
              .join(', ')}`}
            checked={wantDescriptions}
            onToggle={() => setWantDescriptions((v) => !v)}
          />
        )}

        {extras.hasNote && (
          <Row
            testId="terminal-extras-note"
            icon={<StickyNote size={14} className="text-slate-400" />}
            title="Sale note"
            detail={`"${extras.note}"`}
            checked={wantNote}
            onToggle={() => setWantNote((v) => !v)}
          />
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => onConfirm?.({ descriptions: false, note: false })}
            data-testid="terminal-extras-skip"
            className="flex-1 py-3 rounded-xl border-2 border-slate-200 text-slate-600 text-sm font-semibold active:scale-95 transition-all"
          >
            Skip extras
          </button>
          <button
            type="button"
            onClick={() => onConfirm?.({ descriptions: wantDescriptions, note: wantNote })}
            data-testid="terminal-extras-print"
            className="flex-1 py-3 rounded-xl bg-[#1A4D2E] text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-95 transition-all"
          >
            <Printer size={15} />
            Print
          </button>
        </div>

        <button
          type="button"
          onClick={onCancel}
          data-testid="terminal-extras-cancel"
          className="w-full py-2.5 text-sm text-slate-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
